import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, money, dateFR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronLeft, FileDown, Trash2, Plus, Save, Wrench, Pencil, AlertTriangle, Download, Ban, RotateCcw } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import Plate from "@/components/Plate";
import TaskList from "@/components/TaskList";
import { generateInvoicePDF, generateWorkOrderPDF } from "@/lib/pdf";
import { downloadXlsx, fmtDate, fmtMoney, fmtBool, todayStamp } from "@/lib/csv";
import Combobox from "@/components/Combobox";
import ScanDocumentBox from "@/components/ScanDocumentBox";
import { MARQUES, getModelesForMarque } from "@/lib/marques";
import DeletePaiementDialog from "@/components/DeletePaiementDialog";
import NewClientDialog from "@/components/NewClientDialog";
import { isoDate } from "@/lib/date";

const emptyPay = { montant: "", methode: "Espèces", date_paiement: isoDate(), notes: "" };
const emptyInter = {
  date: isoDate(),
  type: "Entretien",
  description: "",
  pieces: "",
  main_oeuvre: "",
  statut: "a_faire",
  mecanicien: "",
  notes: "",
};

const interStatusBadge = {
  a_faire: { txt: "À faire", cls: "bg-slate-100 text-slate-700 border border-slate-200" },
  en_cours: { txt: "En cours", cls: "bg-amber-50 text-amber-700 border border-amber-200" },
  termine: { txt: "Terminée", cls: "bg-emerald-50 text-emerald-700 border border-emerald-200" },
};

export default function VenteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vente, setVente] = useState(null);
  const [paiements, setPaiements] = useState([]);
  const [interventions, setInterventions] = useState([]);

  // Editable form state (always editable)
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  // Paiement form
  const [pay, setPay] = useState(emptyPay);
  const [savingPay, setSavingPay] = useState(false);

  // Delete paiement (password protected)
  const [deletePaiementOpen, setDeletePaiementOpen] = useState(false);
  const [deletePaiementTarget, setDeletePaiementTarget] = useState(null);
  // Client info (full record)
  const [clientInfo, setClientInfo] = useState(null);
  const [companySettings, setCompanySettings] = useState({});

  // Edit paiement dialog
  const [editPayOpen, setEditPayOpen] = useState(false);
  const [editPayTarget, setEditPayTarget] = useState(null);
  const [editPayForm, setEditPayForm] = useState({ montant: "", methode: "Espèces", date_paiement: "", notes: "" });
  const [savingEditPay, setSavingEditPay] = useState(false);
  // Link client dialog
  const [linkClientOpen, setLinkClientOpen] = useState(false);
  const [allClients, setAllClients] = useState([]);
  const [linkClientId, setLinkClientId] = useState("");

  // Timeline / Suivi
  const [newSuiviNote, setNewSuiviNote] = useState("");
  const [savingSuivi, setSavingSuivi] = useState(false);

  // Intervention dialog
  const [interOpen, setInterOpen] = useState(false);
  const [interEditing, setInterEditing] = useState(null);
  const [interForm, setInterForm] = useState(emptyInter);

  const load = async () => {
    const [v, p, i] = await Promise.all([
      api.get(`/ventes/${id}`),
      api.get("/paiements", { params: { vente_id: id } }),
      api.get("/interventions", { params: { vente_id: id } }),
    ]);
    setVente(v.data);
    setForm({
      client_nom: v.data.client_nom || "",
      marque: v.data.marque || "",
      modele: v.data.modele || "",
      annee: v.data.annee || "",
      plaque: v.data.plaque || "",
      vin: v.data.vin || "",
      couleur: v.data.couleur || "",
      kilometrage: v.data.kilometrage || "",
      prix_vente: v.data.prix_vente || "",
      date_vente: v.data.date_vente || "",
      numero_vo: v.data.numero_vo || "",
      date_depart: v.data.date_depart || "",
      carte_grise_disponible: !!v.data.carte_grise_disponible,
      preparer_a_livrer: !!v.data.preparer_a_livrer,
      petite_note: v.data.petite_note || "",
      notes: v.data.notes || "",
    });
    setPaiements(p.data);
    setInterventions(i.data);
    // Fetch client info if linked
    if (v.data.client_id) {
      try {
        const { data: c } = await api.get(`/clients/${v.data.client_id}`);
        setClientInfo(c);
      } catch (err) {
        console.warn("Client lookup failed:", err);
        setClientInfo(null);
      }
    } else {
      setClientInfo(null);
    }
  };

  const openLinkClient = async () => {
    try {
      const { data } = await api.get("/clients");
      setAllClients(data);
    } catch (err) {
      console.warn("Clients list failed:", err);
    }
    setLinkClientId(vente?.client_id || "");
    setLinkClientOpen(true);
  };

  const handleClientCreated = async (newClient) => {
    setAllClients((prev) => [newClient, ...prev]);
    setLinkClientId(newClient.id);
    // Auto-link the new client immediately
    await api.put(`/ventes/${id}`, { client_id: newClient.id, client_nom: newClient.nom });
    toast.success(`Client "${newClient.nom}" lié à la vente`);
    setLinkClientOpen(false);
    load();
  };

  const submitLinkClient = async () => {
    if (!linkClientId) return;
    const c = allClients.find((x) => x.id === linkClientId);
    try {
      await api.put(`/ventes/${id}`, { client_id: linkClientId, client_nom: c?.nom || vente?.client_nom });
      toast.success("Client lié");
      setLinkClientOpen(false);
      load();
    } catch {
      toast.error("Erreur lors du lien");
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  // Load company settings once for PDF rendering
  useEffect(() => {
    api.get("/settings/company").then(({ data }) => setCompanySettings(data || {})).catch(() => {});
  }, []);

  // ---------- Suivi (timeline) actions ----------
  const addSuiviNote = async (e) => {
    e?.preventDefault();
    if (!newSuiviNote.trim()) return;
    setSavingSuivi(true);
    try {
      await api.post(`/ventes/${id}/notes`, { text: newSuiviNote.trim() });
      setNewSuiviNote("");
      toast.success("Note ajoutée");
      load();
    } catch {
      toast.error("Erreur");
    }
    setSavingSuivi(false);
  };

  const removeSuiviNote = async (noteId) => {
    if (!window.confirm("Supprimer cette note ?")) return;
    try {
      await api.delete(`/ventes/${id}/notes/${noteId}`);
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  const saveVente = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        annee: form.annee ? Number(form.annee) : null,
        kilometrage: form.kilometrage ? Number(form.kilometrage) : 0,
        prix_vente: Number(form.prix_vente || 0),
        carte_grise_disponible: !!form.carte_grise_disponible,
      };
      const { data } = await api.put(`/ventes/${id}`, payload);
      setVente(data);
      toast.success("Fiche mise à jour");
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  const addPaiement = async (e) => {
    e.preventDefault();
    if (savingPay) return; // anti double-clic : évite les paiements en double
    setSavingPay(true);
    try {
      await api.post("/paiements", {
        vente_id: id,
        montant: Number(pay.montant || 0),
        methode: pay.methode,
        date_paiement: pay.date_paiement,
        notes: pay.notes,
      });
      toast.success("Paiement enregistré");
      setPay(emptyPay);
      load();
    } catch { toast.error("Erreur lors de l'enregistrement"); }
    finally { setSavingPay(false); }
  };

  const removePaiement = (p) => {
    setDeletePaiementTarget(p);
    setDeletePaiementOpen(true);
  };

  // Interventions
  const openInterCreate = () => { setInterEditing(null); setInterForm(emptyInter); setInterOpen(true); };
  const openInterEdit = (it) => {
    setInterEditing(it);
    setInterForm({
      date: it.date || "",
      type: it.type || "Entretien",
      description: it.description || "",
      pieces: it.pieces ?? "",
      main_oeuvre: it.main_oeuvre ?? "",
      statut: it.statut || "a_faire",
      mecanicien: it.mecanicien || "",
      notes: it.notes || "",
    });
    setInterOpen(true);
  };

  const saveIntervention = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        vente_id: id,
        date: interForm.date,
        type: interForm.type,
        description: interForm.description,
        pieces: Number(interForm.pieces || 0),
        main_oeuvre: Number(interForm.main_oeuvre || 0),
        statut: interForm.statut,
        mecanicien: interForm.mecanicien,
        notes: interForm.notes,
      };
      if (interEditing) {
        const { vente_id, ...rest } = payload;
        await api.put(`/interventions/${interEditing.id}`, rest);
        toast.success("Intervention mise à jour");
      } else {
        await api.post("/interventions", payload);
        toast.success("Intervention ajoutée");
      }
      setInterOpen(false);
      load();
    } catch { toast.error("Erreur"); }
  };

  const removeIntervention = async (it) => {
    if (!window.confirm("Supprimer cette intervention ?")) return;
    await api.delete(`/interventions/${it.id}`);
    load();
  };

  if (!vente || !form) return <div className="text-sm text-muted-foreground">Chargement…</div>;

  const totalMecanique = interventions.reduce((s, it) => s + Number(it.cout_total || 0), 0);

  return (
    <div className="space-y-6">
      <Link to="/ventes" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground" data-testid="vente-back">
        <ChevronLeft size={14} /> Retour aux ventes
      </Link>

      <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-semibold">
            Fiche vente · toujours modifiable
          </div>
          <h1 className="font-display font-black text-4xl tracking-tighter mt-1">
            {vente.marque} {vente.modele} {vente.annee ? `(${vente.annee})` : ""}
          </h1>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <Plate value={vente.plaque} />
            <StatusBadge statut={vente.statut} />
            {!vente.carte_grise_disponible && (
              <span
                data-testid="cg-warning"
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-[11px] font-semibold uppercase tracking-wider bg-red-50 text-destructive border border-red-200"
              >
                <AlertTriangle size={12} /> Carte grise manquante
              </span>
            )}
            {vente.preparer_a_livrer && !vente.atelier_only && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-[11px] font-semibold uppercase tracking-wider bg-emerald-50 text-emerald-800 border border-emerald-300">
                Prêt à livrer
              </span>
            )}
            {vente.date_depart && !vente.atelier_only && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-sm text-[11px] font-semibold uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200">
                Départ : {dateFR(vente.date_depart)}
              </span>
            )}
            {vente.atelier_only && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-sm text-[11px] font-bold uppercase tracking-wider bg-klein/10 text-klein border border-klein/30">
                VO Atelier
              </span>
            )}
            {vente.petite_note && (
              <span
                data-testid="vente-petite-note-badge"
                className="inline-flex items-start gap-1.5 px-2.5 py-1 rounded-sm text-[11px] font-semibold bg-amber-50 text-amber-900 border border-amber-200 max-w-md"
                title={vente.petite_note}
              >
                📝 <span className="truncate">{vente.petite_note}</span>
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            data-testid="vente-pdf-btn"
            onClick={() => generateInvoicePDF(vente, paiements, companySettings)}
            variant="outline"
            className="rounded-sm"
          >
            <FileDown size={14} className="mr-1" /> Reçu PDF
          </Button>
          <Button
            data-testid="vente-excel-btn"
            onClick={() => {
              const rows = [
                { section: "VENTE", champ: "Plaque", valeur: vente.plaque },
                { section: "VENTE", champ: "Marque", valeur: vente.marque },
                { section: "VENTE", champ: "Modèle", valeur: vente.modele },
                { section: "VENTE", champ: "Année", valeur: vente.annee || "" },
                { section: "VENTE", champ: "VIN", valeur: vente.vin || "" },
                { section: "VENTE", champ: "Couleur", valeur: vente.couleur || "" },
                { section: "VENTE", champ: "Kilométrage", valeur: vente.kilometrage || "" },
                { section: "VENTE", champ: "N° VO", valeur: vente.numero_vo || "" },
                { section: "VENTE", champ: "Date vente", valeur: fmtDate(vente.date_vente) },
                { section: "VENTE", champ: "Date départ", valeur: fmtDate(vente.date_depart) },
                { section: "VENTE", champ: "Carte grise", valeur: fmtBool(vente.carte_grise_disponible) },
                { section: "CLIENT", champ: "Nom", valeur: vente.client_nom },
                { section: "CLIENT", champ: "Téléphone", valeur: vente.client_telephone || "" },
                { section: "TOTAL", champ: "Prix véhicule", valeur: fmtMoney(vente.prix_vente) },
                { section: "TOTAL", champ: "Coût interventions", valeur: fmtMoney(vente.cout_interventions || 0) },
                { section: "TOTAL", champ: "Total à payer", valeur: fmtMoney(vente.prix_total || vente.prix_vente) },
                { section: "TOTAL", champ: "Encaissé", valeur: fmtMoney(vente.montant_paye) },
                { section: "TOTAL", champ: "Restant dû", valeur: fmtMoney(vente.montant_restant) },
              ];
              paiements.forEach((p, i) => {
                rows.push({ section: `PAIEMENT ${i + 1}`, champ: "Date", valeur: fmtDate(p.date_paiement) });
                rows.push({ section: `PAIEMENT ${i + 1}`, champ: "Montant", valeur: fmtMoney(p.montant) });
                rows.push({ section: `PAIEMENT ${i + 1}`, champ: "Méthode", valeur: p.methode });
                rows.push({ section: `PAIEMENT ${i + 1}`, champ: "Notes", valeur: p.notes || "" });
              });
              interventions.forEach((it, i) => {
                rows.push({ section: `INTERVENTION ${i + 1}`, champ: "Date", valeur: fmtDate(it.date) });
                rows.push({ section: `INTERVENTION ${i + 1}`, champ: "Type", valeur: it.type });
                rows.push({ section: `INTERVENTION ${i + 1}`, champ: "Description", valeur: it.description });
                rows.push({ section: `INTERVENTION ${i + 1}`, champ: "Coût total", valeur: fmtMoney(it.cout_total) });
                rows.push({ section: `INTERVENTION ${i + 1}`, champ: "Statut", valeur: it.statut });
                rows.push({ section: `INTERVENTION ${i + 1}`, champ: "Payée", valeur: fmtBool(it.paye) });
              });
              const columns = [
                { key: "section", label: "Section" },
                { key: "champ", label: "Champ" },
                { key: "valeur", label: "Valeur" },
              ];
              downloadXlsx(`vente-${vente.plaque}-${todayStamp()}`, columns, rows, `Vente ${vente.plaque}`);
              toast.success("Dossier exporté en Excel");
            }}
            variant="outline"
            className="rounded-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          >
            <Download size={14} className="mr-1" /> Excel
          </Button>
          {!vente.cancelled && !vente.atelier_only && (
            <Button
              data-testid="vente-cancel-btn"
              onClick={async () => {
                const reason = window.prompt("Motif de l'annulation (optionnel) :", "");
                if (reason === null) return; // user cancelled
                if (!window.confirm(`Annuler définitivement la vente ${vente.plaque} ?\n\nLa vente sera déplacée dans 'Ventes annulées' et exclue du dashboard. Vous pouvez la restaurer à tout moment.`)) return;
                try {
                  await api.put(`/ventes/${vente.id}`, { cancelled: true, cancelled_reason: reason || "" });
                  toast.success("Vente annulée et archivée");
                  navigate("/ventes-annulees");
                } catch {
                  toast.error("Erreur");
                }
              }}
              variant="outline"
              className="rounded-sm border-red-300 text-red-700 hover:bg-red-50"
            >
              <Ban size={14} className="mr-1" /> Annuler la vente
            </Button>
          )}
          {vente.cancelled && (
            <Button
              data-testid="vente-restore-btn"
              onClick={async () => {
                if (!window.confirm(`Restaurer la vente ${vente.plaque} dans Ventes en cours ?`)) return;
                try {
                  await api.put(`/ventes/${vente.id}`, { cancelled: false, cancelled_reason: "" });
                  toast.success("Vente restaurée");
                  load();
                } catch {
                  toast.error("Erreur");
                }
              }}
              variant="outline"
              className="rounded-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            >
              <RotateCcw size={14} className="mr-1" /> Restaurer la vente
            </Button>
          )}
        </div>
      </header>

      {/* KPI */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <div className="bg-card border border-border rounded-sm p-6">
          <div className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-semibold">
            {vente.cout_interventions > 0 ? "Total à payer" : "Prix de vente"}
          </div>
          <div className="font-display font-black text-2xl tracking-tighter mt-2" data-testid="kpi-total">
            {money(vente.prix_total != null ? vente.prix_total : vente.prix_vente)}
          </div>
          {vente.cout_interventions > 0 && (
            <div className="text-[11px] text-muted-foreground mt-1 leading-relaxed" data-testid="kpi-total-breakdown">
              {money(vente.prix_vente)} véhicule
              <span className="text-amber-700"> + {money(vente.cout_interventions)} mécanique</span>
            </div>
          )}
        </div>
        <div className="bg-card border border-border rounded-sm p-6">
          <div className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-semibold">Encaissé</div>
          <div className="font-display font-black text-2xl tracking-tighter mt-2 text-emerald-700">{money(vente.montant_paye)}</div>
        </div>
        <div className="bg-card border border-border rounded-sm p-6">
          <div className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-semibold">Restant dû</div>
          <div className={`font-display font-black text-2xl tracking-tighter mt-2 ${vente.montant_restant > 0 ? "text-destructive" : "text-emerald-700"}`}>{money(vente.montant_restant)}</div>
        </div>
        <div className="bg-card border border-border rounded-sm p-6">
          <div className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-semibold">Coût mécanique</div>
          <div className="font-display font-black text-2xl tracking-tighter mt-2 text-amber-700">{money(totalMecanique)}</div>
        </div>
      </div>

      {/* Fiche client (toujours visible) */}
      <div className="bg-card border border-border rounded-sm p-6" data-testid="client-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] tracking-[0.2em] uppercase text-klein font-semibold">
              Fiche client
            </div>
            <h3 className="font-display font-bold text-xl mt-0.5">
              {clientInfo ? clientInfo.nom : (vente.client_nom || "Aucun client")}
            </h3>
            {!clientInfo && vente.client_nom && (
              <p className="text-xs text-amber-700 mt-1">
                Nom enregistré sur la vente, mais aucune fiche client liée.
              </p>
            )}
            {!clientInfo && !vente.client_nom && (
              <p className="text-xs text-muted-foreground mt-1">
                Aucun client n'est encore associé à cette vente.
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={openLinkClient}
              data-testid="client-card-link-btn"
              className="rounded-sm"
            >
              {clientInfo ? "Changer le client" : "Lier un client"}
            </Button>
            {clientInfo && (
              <Link
                to="/clients"
                className="text-xs text-klein hover:underline"
                data-testid="client-card-edit"
              >
                Voir tous les clients →
              </Link>
            )}
          </div>
        </div>
        {clientInfo && (
          <dl className="grid grid-cols-1 md:grid-cols-4 gap-x-6 gap-y-3 text-sm mt-4">
            <div>
              <dt className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Téléphone</dt>
              <dd className="mt-0.5 font-medium">
                {clientInfo.telephone
                  ? <a href={`tel:${clientInfo.telephone}`} className="text-klein hover:underline">{clientInfo.telephone}</a>
                  : "—"}
              </dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Email</dt>
              <dd className="mt-0.5 font-medium">
                {clientInfo.email
                  ? <a href={`mailto:${clientInfo.email}`} className="text-klein hover:underline">{clientInfo.email}</a>
                  : "—"}
              </dd>
            </div>
            <div className="md:col-span-2">
              <dt className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Adresse</dt>
              <dd className="mt-0.5 font-medium">{clientInfo.adresse || "—"}</dd>
            </div>
            {clientInfo.notes && (
              <div className="md:col-span-4">
                <dt className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Notes client</dt>
                <dd className="mt-0.5 text-muted-foreground">{clientInfo.notes}</dd>
              </div>
            )}
          </dl>
        )}
      </div>

      {/* Dialog: link a client */}
      <Dialog open={linkClientOpen} onOpenChange={setLinkClientOpen}>
        <DialogContent className="max-w-md" data-testid="link-client-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Lier un client à la vente</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Client existant</Label>
              <Select value={linkClientId || undefined} onValueChange={setLinkClientId}>
                <SelectTrigger data-testid="link-client-select" className="rounded-sm mt-1">
                  <SelectValue placeholder="Sélectionner…" />
                </SelectTrigger>
                <SelectContent>
                  {allClients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="text-center text-xs text-muted-foreground">— ou —</div>
            <NewClientDialog
              onCreated={handleClientCreated}
              triggerLabel="Créer un nouveau client"
              testId="link-client-newbtn"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              onClick={submitLinkClient}
              disabled={!linkClientId || linkClientId === vente.client_id}
              data-testid="link-client-submit"
              className="bg-klein hover:bg-blue-800 text-white rounded-sm"
            >
              Lier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Editable info form (always editable) */}
      <form onSubmit={saveVente} className="bg-card border border-border rounded-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-semibold">Informations</div>
            <h3 className="font-display font-bold text-xl">Fiche véhicule & vente</h3>
          </div>
          <Button type="submit" disabled={saving} data-testid="vente-save-btn" className="bg-klein hover:bg-blue-800 text-white rounded-sm">
            <Save size={14} className="mr-1" /> {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </div>

        <ScanDocumentBox
          label="Pré-remplir depuis la carte grise"
          onExtract={(data) => {
            setForm((f) => ({
              ...f,
              marque: data.marque || f.marque,
              modele: data.modele || f.modele,
              annee: data.annee || f.annee,
              plaque: (data.plaque || f.plaque || "").replace(/-/g, "").toUpperCase(),
              vin: data.vin || f.vin,
              couleur: data.couleur || f.couleur,
              kilometrage: data.kilometrage ?? f.kilometrage,
            }));
          }}
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider">Client</Label>
            <Input data-testid="edit-client-nom" value={form.client_nom} onChange={(e) => setForm({ ...form, client_nom: e.target.value })} className="mt-1 rounded-sm" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Date vente</Label>
            <Input data-testid="edit-date" type="date" value={form.date_vente} onChange={(e) => setForm({ ...form, date_vente: e.target.value })} className="mt-1 rounded-sm" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Prix de vente</Label>
            <Input data-testid="edit-prix" type="number" inputMode="decimal" step="0.01" value={form.prix_vente} onChange={(e) => setForm({ ...form, prix_vente: e.target.value })} className="mt-1 rounded-sm" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Marque</Label>
            <Combobox
              testId="edit-marque"
              value={form.marque}
              onChange={(v) => setForm({ ...form, marque: v })}
              options={MARQUES}
              placeholder="Tapez ou choisissez…"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Modèle</Label>
            <Combobox
              testId="edit-modele"
              value={form.modele}
              onChange={(v) => setForm({ ...form, modele: v })}
              options={getModelesForMarque(form.marque)}
              placeholder={form.marque ? "Tapez ou choisissez…" : "Saisissez la marque d'abord"}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Année</Label>
            <Input data-testid="edit-annee" type="number" inputMode="decimal" value={form.annee} onChange={(e) => setForm({ ...form, annee: e.target.value })} className="mt-1 rounded-sm" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Plaque</Label>
            <Input data-testid="edit-plaque" value={form.plaque} onChange={(e) => setForm({ ...form, plaque: e.target.value.toUpperCase() })} className="mt-1 rounded-sm font-mono uppercase tracking-widest" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">VIN</Label>
            <Input data-testid="edit-vin" value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value })} className="mt-1 rounded-sm" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Couleur</Label>
            <Input data-testid="edit-couleur" value={form.couleur} onChange={(e) => setForm({ ...form, couleur: e.target.value })} className="mt-1 rounded-sm" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Kilométrage</Label>
            <Input data-testid="edit-km" type="number" inputMode="decimal" value={form.kilometrage} onChange={(e) => setForm({ ...form, kilometrage: e.target.value })} className="mt-1 rounded-sm" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">N° VO (stock)</Label>
            <Input data-testid="edit-numero-vo" value={form.numero_vo} onChange={(e) => setForm({ ...form, numero_vo: e.target.value })} className="mt-1 rounded-sm" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wider flex items-center gap-1.5">
              📝 Petite note (rappel rapide)
            </Label>
            <Input
              data-testid="edit-petite-note"
              maxLength={120}
              placeholder="Ex : VIP · À rappeler demain · Acompte versé…"
              value={form.petite_note}
              onChange={(e) => setForm({ ...form, petite_note: e.target.value })}
              className="mt-1 rounded-sm"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Max. 120 caractères · Affiché en jaune dans la liste des ventes.
            </p>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wider">Notes</Label>
            <Textarea data-testid="edit-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1 rounded-sm" />
          </div>
          {!vente.atelier_only && (
            <div>
              <Label className="text-xs uppercase tracking-wider">Date de départ</Label>
              <Input data-testid="edit-date-depart" type="date" value={form.date_depart || ""} onChange={(e) => setForm({ ...form, date_depart: e.target.value })} className="mt-1 rounded-sm" />
            </div>
          )}
          <div className="md:col-span-3">
            <label className="inline-flex items-center gap-2 px-3 py-2 border border-border rounded-sm cursor-pointer select-none mr-3">
              <input
                type="checkbox"
                data-testid="edit-cartegrise"
                checked={!!form.carte_grise_disponible}
                onChange={(e) => setForm({ ...form, carte_grise_disponible: e.target.checked })}
                className="w-4 h-4 accent-klein"
              />
              <span className="text-sm font-medium">Carte grise disponible</span>
            </label>
            {!vente.atelier_only && (
              <label className="inline-flex items-center gap-2 px-3 py-2 border border-border rounded-sm cursor-pointer select-none bg-amber-50/40">
                <input
                  type="checkbox"
                  data-testid="edit-preparer"
                  checked={!!form.preparer_a_livrer}
                  onChange={(e) => setForm({ ...form, preparer_a_livrer: e.target.checked })}
                  className="w-4 h-4 accent-klein"
                />
                <span className="text-sm font-medium">Prêt à livrer</span>
              </label>
            )}
          </div>
        </div>
      </form>

      {/* Tâches internes */}
      <TaskList venteId={id} title="Tâches & rappels de cette vente" />

      {/* Suivi / Historique de notes */}
      <div className="bg-card border border-border rounded-sm p-5" data-testid="vente-suivi-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-semibold">Suivi</div>
            <h3 className="font-display font-bold text-xl">Historique des notes</h3>
          </div>
          {Array.isArray(vente.historique) && vente.historique.length > 0 && (
            <span className="text-xs text-muted-foreground">
              {vente.historique.length} note{vente.historique.length > 1 ? "s" : ""}
            </span>
          )}
        </div>

        <form onSubmit={addSuiviNote} className="flex flex-col sm:flex-row gap-2 mb-4">
          <Textarea
            data-testid="vente-suivi-input"
            rows={1}
            placeholder="Ex : Client a rappelé, demande de livrer mardi à 17h…"
            value={newSuiviNote}
            onChange={(e) => setNewSuiviNote(e.target.value)}
            className="rounded-sm flex-1 min-h-[40px]"
          />
          <Button
            type="submit"
            data-testid="vente-suivi-add"
            disabled={savingSuivi || !newSuiviNote.trim()}
            className="bg-klein hover:bg-blue-800 text-white rounded-sm shrink-0"
          >
            {savingSuivi ? "Ajout…" : "Ajouter"}
          </Button>
        </form>

        {(!vente.historique || vente.historique.length === 0) ? (
          <div className="text-sm text-muted-foreground italic py-4 text-center border border-dashed border-border rounded-sm">
            Aucune note. Ajoutez-en pour suivre l'évolution de cette vente.
          </div>
        ) : (
          <ol className="space-y-2 max-h-80 overflow-y-auto pr-1">
            {[...vente.historique].reverse().map((h) => (
              <li key={h.id} className="border-l-2 border-klein/40 pl-3 py-1 group">
                <div className="text-[11px] text-muted-foreground flex items-center justify-between gap-2">
                  <span>
                    {new Date(h.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                    {h.author ? ` · ${h.author}` : ""}
                  </span>
                  <button
                    onClick={() => removeSuiviNote(h.id)}
                    className="opacity-0 group-hover:opacity-100 text-destructive hover:underline text-[11px]"
                    title="Supprimer cette note"
                  >
                    supprimer
                  </button>
                </div>
                <div className="text-sm whitespace-pre-wrap mt-0.5">{h.text}</div>
              </li>
            ))}
          </ol>
        )}
      </div>

      {/* Paiements + ajout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-card border border-border rounded-sm overflow-hidden lg:col-span-2">
          <div className="px-6 py-4 border-b flex items-center justify-between">
            <div>
              <div className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-semibold">Encaissements</div>
              <h3 className="font-display font-bold text-xl">Historique des paiements</h3>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-6 py-3">Date</th>
                  <th className="px-6 py-3">Méthode</th>
                  <th className="px-6 py-3">Notes</th>
                  <th className="px-6 py-3 text-right">Montant</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="paiements-list">
                {paiements.length === 0 && (
                  <tr><td colSpan={5} className="px-6 py-10 text-center text-muted-foreground">Aucun paiement.</td></tr>
                )}
                {paiements.map((p) => (
                  <tr key={p.id} className="border-t hover:bg-slate-50">
                    <td className="px-6 py-3">{dateFR(p.date_paiement)}</td>
                    <td className="px-6 py-3">{p.methode}</td>
                    <td className="px-6 py-3 text-muted-foreground">{p.notes || "—"}</td>
                    <td className="px-6 py-3 text-right font-mono">{money(p.montant)}</td>
                    <td className="px-6 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => {
                          setEditPayTarget(p);
                          setEditPayForm({
                            montant: String(p.montant ?? ""),
                            methode: p.methode || "Espèces",
                            date_paiement: p.date_paiement || isoDate(),
                            notes: p.notes || "",
                          });
                          setEditPayOpen(true);
                        }}
                        data-testid={`paiement-edit-${p.id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-klein/5 hover:bg-klein/10 text-klein border border-klein/20 rounded-sm mr-1 font-semibold"
                      >
                        <Pencil size={12} /> Modifier
                      </button>
                      <button onClick={() => removePaiement(p)} data-testid={`paiement-delete-${p.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs text-destructive hover:bg-red-50 rounded-sm">
                        <Trash2 size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Edit paiement dialog */}
        <Dialog open={editPayOpen} onOpenChange={setEditPayOpen}>
          <DialogContent className="max-w-md" data-testid="vente-paiement-edit-dialog">
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight">Modifier le paiement</DialogTitle>
            </DialogHeader>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!editPayTarget) return;
                setSavingEditPay(true);
                try {
                  await api.put(`/paiements/${editPayTarget.id}`, {
                    montant: Number(editPayForm.montant || 0),
                    methode: editPayForm.methode,
                    date_paiement: editPayForm.date_paiement,
                    notes: editPayForm.notes,
                  });
                  toast.success("Paiement mis à jour");
                  setEditPayOpen(false);
                  load();
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Erreur");
                } finally {
                  setSavingEditPay(false);
                }
              }}
              className="space-y-3"
            >
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Montant *</Label>
                  <Input data-testid="vente-paie-edit-montant" type="number" inputMode="decimal" step="0.01" required value={editPayForm.montant} onChange={(e) => setEditPayForm({ ...editPayForm, montant: e.target.value })} className="mt-1 rounded-sm" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Méthode</Label>
                  <Select value={editPayForm.methode} onValueChange={(v) => setEditPayForm({ ...editPayForm, methode: v })}>
                    <SelectTrigger data-testid="vente-paie-edit-methode" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Espèces">Espèces</SelectItem>
                      <SelectItem value="Virement">Virement</SelectItem>
                      <SelectItem value="Chèque">Chèque</SelectItem>
                      <SelectItem value="Carte">Carte</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Date</Label>
                <Input data-testid="vente-paie-edit-date" type="date" value={editPayForm.date_paiement} onChange={(e) => setEditPayForm({ ...editPayForm, date_paiement: e.target.value })} className="mt-1 rounded-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Détail / Notes</Label>
                <Textarea data-testid="vente-paie-edit-notes" value={editPayForm.notes} onChange={(e) => setEditPayForm({ ...editPayForm, notes: e.target.value })} rows={3} className="mt-1 rounded-sm" />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={savingEditPay} data-testid="vente-paie-edit-submit" className="bg-klein hover:bg-blue-800 text-white rounded-sm">
                  {savingEditPay ? "Enregistrement…" : "Enregistrer"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <form onSubmit={addPaiement} className="bg-card border border-border rounded-sm p-6 space-y-3">
          <h3 className="font-display font-bold text-xl">Ajouter un paiement</h3>
          <p className="text-xs text-muted-foreground">Lié automatiquement à cette vente · {vente.plaque}</p>
          <div>
            <Label className="text-xs uppercase tracking-wider">Montant *</Label>
            <Input data-testid="paiement-form-montant" type="number" inputMode="decimal" step="0.01" value={pay.montant} onChange={(e) => setPay({ ...pay, montant: e.target.value })} required className="mt-1 rounded-sm" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Méthode</Label>
            <Select value={pay.methode} onValueChange={(v) => setPay({ ...pay, methode: v })}>
              <SelectTrigger data-testid="paiement-form-methode" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Espèces">Espèces</SelectItem>
                <SelectItem value="Virement">Virement</SelectItem>
                <SelectItem value="Chèque">Chèque</SelectItem>
                <SelectItem value="Carte">Carte</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Date</Label>
            <Input data-testid="paiement-form-date" type="date" value={pay.date_paiement} onChange={(e) => setPay({ ...pay, date_paiement: e.target.value })} className="mt-1 rounded-sm" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Notes</Label>
            <Textarea data-testid="paiement-form-notes" value={pay.notes} onChange={(e) => setPay({ ...pay, notes: e.target.value })} rows={2} className="mt-1 rounded-sm" />
          </div>
          <Button type="submit" disabled={savingPay} data-testid="paiement-form-submit" className="w-full bg-klein hover:bg-blue-800 text-white rounded-sm">
            <Plus size={14} className="mr-1" /> {savingPay ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </form>
      </div>

      {/* Mécanique */}
      <div className="bg-card border border-border rounded-sm">
        <div className="px-6 py-4 border-b flex items-center justify-between gap-2 flex-wrap">
          <div>
            <div className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-semibold">Atelier</div>
            <h3 className="font-display font-bold text-xl flex items-center gap-2">
              <Wrench size={18} /> Partie mécanique
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => generateWorkOrderPDF(vente, interventions)}
              disabled={interventions.length === 0}
              data-testid="workorder-pdf-btn"
              className="rounded-sm"
            >
              <FileDown size={14} className="mr-1" /> Fiche atelier (PDF)
            </Button>
            <Dialog open={interOpen} onOpenChange={setInterOpen}>
            <DialogTrigger asChild>
              <Button onClick={openInterCreate} data-testid="inter-add-btn" className="bg-klein hover:bg-blue-800 text-white rounded-sm">
                <Plus size={14} className="mr-1" /> Nouvelle intervention
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight">
                  {interEditing ? "Modifier l'intervention" : "Nouvelle intervention"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={saveIntervention} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Date</Label>
                    <Input data-testid="inter-form-date" type="date" value={interForm.date} onChange={(e) => setInterForm({ ...interForm, date: e.target.value })} className="mt-1 rounded-sm" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Type</Label>
                    <Select value={interForm.type} onValueChange={(v) => setInterForm({ ...interForm, type: v })}>
                      <SelectTrigger data-testid="inter-form-type" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Entretien">Entretien</SelectItem>
                        <SelectItem value="Réparation">Réparation</SelectItem>
                        <SelectItem value="Contrôle">Contrôle</SelectItem>
                        <SelectItem value="Diagnostic">Diagnostic</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Description *</Label>
                  <Textarea data-testid="inter-form-desc" required value={interForm.description} onChange={(e) => setInterForm({ ...interForm, description: e.target.value })} rows={2} className="mt-1 rounded-sm" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Pièces (€)</Label>
                    <Input data-testid="inter-form-pieces" type="number" inputMode="decimal" step="0.01" value={interForm.pieces} onChange={(e) => setInterForm({ ...interForm, pieces: e.target.value })} className="mt-1 rounded-sm" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Main d'œuvre (€)</Label>
                    <Input data-testid="inter-form-mo" type="number" inputMode="decimal" step="0.01" value={interForm.main_oeuvre} onChange={(e) => setInterForm({ ...interForm, main_oeuvre: e.target.value })} className="mt-1 rounded-sm" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Statut</Label>
                    <Select value={interForm.statut} onValueChange={(v) => setInterForm({ ...interForm, statut: v })}>
                      <SelectTrigger data-testid="inter-form-statut" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="a_faire">À faire</SelectItem>
                        <SelectItem value="en_cours">En cours</SelectItem>
                        <SelectItem value="termine">Terminée</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Mécanicien</Label>
                    <Input data-testid="inter-form-meca" value={interForm.mecanicien} onChange={(e) => setInterForm({ ...interForm, mecanicien: e.target.value })} className="mt-1 rounded-sm" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Notes</Label>
                    <Input data-testid="inter-form-notes" value={interForm.notes} onChange={(e) => setInterForm({ ...interForm, notes: e.target.value })} className="mt-1 rounded-sm" />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="inter-form-submit" className="bg-klein hover:bg-blue-800 text-white rounded-sm">
                    {interEditing ? "Enregistrer" : "Ajouter"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-6 py-3">Date</th>
                <th className="px-6 py-3">Type</th>
                <th className="px-6 py-3">Description</th>
                <th className="px-6 py-3">Mécanicien</th>
                <th className="px-6 py-3 text-right">Pièces</th>
                <th className="px-6 py-3 text-right">M. œuvre</th>
                <th className="px-6 py-3 text-right">Total</th>
                <th className="px-6 py-3 text-center">Statut</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="interventions-list">
              {interventions.length === 0 && (
                <tr><td colSpan={9} className="px-6 py-10 text-center text-muted-foreground">Aucune intervention enregistrée.</td></tr>
              )}
              {interventions.map((it) => {
                const cfg = interStatusBadge[it.statut] || interStatusBadge.termine;
                return (
                  <tr key={it.id} className="border-t hover:bg-slate-50">
                    <td className="px-6 py-3 whitespace-nowrap">{dateFR(it.date)}</td>
                    <td className="px-6 py-3">{it.type}</td>
                    <td className="px-6 py-3 max-w-md">{it.description}</td>
                    <td className="px-6 py-3 text-muted-foreground">{it.mecanicien || "—"}</td>
                    <td className="px-6 py-3 text-right font-mono">{money(it.pieces)}</td>
                    <td className="px-6 py-3 text-right font-mono">{money(it.main_oeuvre)}</td>
                    <td className="px-6 py-3 text-right font-mono font-semibold">{money(it.cout_total)}</td>
                    <td className="px-6 py-3 text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-semibold uppercase tracking-wider ${cfg.cls}`}>
                        {cfg.txt}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-right whitespace-nowrap">
                      <button onClick={() => openInterEdit(it)} data-testid={`inter-edit-${it.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs hover:bg-slate-100 rounded-sm mr-1">
                        <Pencil size={12} />
                      </button>
                      <button onClick={() => removeIntervention(it)} data-testid={`inter-delete-${it.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs text-destructive hover:bg-red-50 rounded-sm">
                        <Trash2 size={12} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <DeletePaiementDialog
        open={deletePaiementOpen}
        onOpenChange={setDeletePaiementOpen}
        paiement={deletePaiementTarget}
        onDeleted={load}
      />
    </div>
  );
}
